import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import Cookies from 'js-cookie'

import { useChangePasswordMutation } from '../../../../../features/auth/authApiSlice'
import { useChangePasswordValidation } from '../../../../../pkg/validators'
import { IChangePassword } from '../../../../../pkg/requests'
import { IRTKQueryResponse } from '../../../../../pkg/UI'

import {
  PageWrapper,
  ContentContainer,
  Card,
  CardHeader,
  FormContainer,
  Form,
  FormGroup,
  Input,
  SubmitButton,
  ErrorMessage,
  StatusMessage,
  LanguageSwitcherContainer,
  LanguageButtonGroup,
  LanguageButton,
} from './changePasswordElements'

const LanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation()
  const currentLanguage = i18n.language

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language)
    Cookies.set('i18next', language, { expires: 365 })
  }

  return (
    <LanguageSwitcherContainer>
      <h3>{t('Root:PasswordChange.Translation')}</h3>
      <LanguageButtonGroup>
        <LanguageButton
          isActive={currentLanguage === 'en'}
          onClick={() => changeLanguage('en')}
        >
          English
        </LanguageButton>
        <LanguageButton
          isActive={currentLanguage === 'pl'}
          onClick={() => changeLanguage('pl')}
        >
          Polski
        </LanguageButton>
      </LanguageButtonGroup>
    </LanguageSwitcherContainer>
  )
}

const PasswordChangeForm: React.FC = () => {
  const ChangePasswordSchema = useChangePasswordValidation()
  const { t } = useTranslation()
  const [changePassword] = useChangePasswordMutation()
  const [submitStatus, setSubmitStatus] = useState<{ message: string; isError: boolean } | null>(
    null,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IChangePassword>({
    resolver: yupResolver(ChangePasswordSchema),
    mode: 'onChange',
  })

  const onSubmit = async (data: IChangePassword) => {
    setIsSubmitting(true)
    try {
      const response: IRTKQueryResponse = await changePassword(data).unwrap()
      if ('data' in response) {
        setSubmitStatus({
          message: response.data.message,
          isError: false,
        })
        reset()
      }
    } catch (error) {
      setSubmitStatus({
        message: t('Root:PasswordChange.ChangePasswordFailed') || 'Password change failed',
        isError: true,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PageWrapper>
      <ContentContainer>
        <LanguageSwitcher />

        <Card>
          <CardHeader>
            <h2>{t('Root:PasswordChange.ChangeYourPassword')}</h2>
          </CardHeader>

          <FormContainer>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                control={control}
                name='password'
                render={({ field }) => (
                  <FormGroup>
                    <label htmlFor='password'>{t('Root:PasswordChange.CurrentPassword')}</label>
                    <Input
                      type='password'
                      id='password'
                      placeholder={
                        t('Root:PasswordChange.CurrentPasswordPlaceholder') ||
                        'Enter your current password'
                      }
                      {...field}
                    />
                    {errors.password && errors.password.message && (
                      <ErrorMessage>{errors.password.message}</ErrorMessage>
                    )}
                  </FormGroup>
                )}
              />

              <Controller
                control={control}
                name='new_password1'
                render={({ field }) => (
                  <FormGroup>
                    <label htmlFor='new_password1'>{t('Root:PasswordChange.NewPassword')}</label>
                    <Input
                      type='password'
                      id='new_password1'
                      placeholder={
                        t('Root:PasswordChange.NewPasswordPlaceholder') || 'Enter new password'
                      }
                      {...field}
                    />
                    {errors.new_password1 && errors.new_password1.message && (
                      <ErrorMessage>{errors.new_password1.message}</ErrorMessage>
                    )}
                  </FormGroup>
                )}
              />

              <Controller
                control={control}
                name='new_password2'
                render={({ field }) => (
                  <FormGroup>
                    <label htmlFor='new_password2'>{t('Root:PasswordChange.RepeatPassword')}</label>
                    <Input
                      type='password'
                      id='new_password2'
                      placeholder={
                        t('Root:PasswordChange.RepeatPasswordPlaceholder') || 'Confirm new password'
                      }
                      {...field}
                    />
                    {errors.new_password2 && errors.new_password2.message && (
                      <ErrorMessage>{errors.new_password2.message}</ErrorMessage>
                    )}
                  </FormGroup>
                )}
              />

              <SubmitButton
                type='submit'
                disabled={isSubmitting}
              >
                {isSubmitting ?
                  t('Root:Common.Processing') || 'Processing...'
                : t('Root:PasswordChange.ChangePassword')}
              </SubmitButton>

              {submitStatus && (
                <StatusMessage isError={submitStatus.isError}>{submitStatus.message}</StatusMessage>
              )}
            </Form>
          </FormContainer>
        </Card>
      </ContentContainer>
    </PageWrapper>
  )
}

export default PasswordChangeForm
